/**
 * Dashboard range presets → from/to + daily-allowed rule (WSRADM-0037).
 *
 * "Today" is the browser's local calendar date — never UTC conversion.
 */

export type RangePreset = '1y' | '6m' | '3m' | 'last' | 'this' | 'custom';

export interface ResolvedRange {
  from: string;
  to: string;
  dailyAllowed: boolean;
}

const PRESETS: ReadonlySet<string> = new Set(['1y', '6m', '3m', 'last', 'this', 'custom']);

/** Local yyyy-MM-dd from a Date (no timezone conversion). */
export function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const d = new Date(year, month, day);
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
  return d;
}

/** Calendar-day difference (to − from). */
function dayDifference(from: string, to: string): number | null {
  const a = parseYmd(from);
  const b = parseYmd(to);
  if (a === null || b === null) return null;
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

/** `(to − from) ≤ 92 days` ⇒ at most 93 calendar days; safe for daily granularity. */
export function dailyAllowed(from: string, to: string): boolean {
  const diff = dayDifference(from, to);
  return diff !== null && diff <= 92;
}

/** yyyy-MM-dd — Sunday in the local calendar. */
export function isSunday(period: string): boolean {
  const d = parseYmd(period);
  return d !== null && d.getDay() === 0;
}

/** yyyy-MM-dd — first of the month. */
export function isFirstOfMonth(period: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(period);
  return match !== null && match[3] === '01';
}

/** Daily chart markers — Sunday and first-of-month are independent (both can apply). */
export type DailyReferenceMarker =
  | { type: 'sunday'; period: string }
  | { type: 'month'; period: string };

export function dailyReferenceMarkers(periods: string[]): DailyReferenceMarker[] {
  const out: DailyReferenceMarker[] = [];
  for (const period of periods) {
    if (isSunday(period)) out.push({ type: 'sunday', period });
    if (isFirstOfMonth(period)) out.push({ type: 'month', period });
  }
  return out;
}

export function parseRangePreset(raw: string | null): RangePreset {
  if (raw !== null && PRESETS.has(raw)) return raw as RangePreset;
  return '3m';
}

/**
 * Resolve a preset (or custom pair) to from/to. Returns null for custom when
 * either date is missing or from > to.
 */
export function resolveRange(
  preset: RangePreset,
  today: Date,
  custom?: { from: string; to: string },
): ResolvedRange | null {
  const todayStr = formatLocalDate(today);

  if (preset === 'custom') {
    const from = custom?.from?.trim() ?? '';
    const to = custom?.to?.trim() ?? '';
    if (from.length === 0 || to.length === 0) return null;
    if (parseYmd(from) === null || parseYmd(to) === null) return null;
    if (from > to) return null;
    return { from, to, dailyAllowed: dailyAllowed(from, to) };
  }

  let from: string;
  let to: string = todayStr;

  if (preset === '1y') {
    from = formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 11, 1));
  } else if (preset === '6m') {
    from = formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 5, 1));
  } else if (preset === '3m') {
    from = formatLocalDate(
      new Date(today.getFullYear(), today.getMonth(), today.getDate() - 92),
    );
  } else if (preset === 'last') {
    const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastPrev = new Date(firstThis.getFullYear(), firstThis.getMonth(), 0);
    from = formatLocalDate(new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1));
    to = formatLocalDate(lastPrev);
  } else {
    // this
    from = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
  }

  return { from, to, dailyAllowed: dailyAllowed(from, to) };
}

/**
 * Shift a YYYY-MM-DD or YYYY-MM date string back one calendar year.
 * Leap day (02-29) maps to 02-28.
 */
export function shiftOneYearBack(ymd: string): string {
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(ymd);
  if (!match) return ymd;
  const year = Number(match[1]) - 1;
  const month = match[2];
  const day = match[3];
  if (day !== undefined) {
    if (month === '02' && day === '29') {
      return `${year}-02-28`;
    }
    return `${year}-${month}-${day}`;
  }
  return `${year}-${month}`;
}
